<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import Popover from 'primevue/popover'
import ToggleSwitch from 'primevue/toggleswitch'

import IconBell from '@/assets/svg/iconBell.svg'
import AppButton from '@/components/atoms/buttons/AppButton.vue'
import { useNotificationSettingsStore } from '@/stores/useNotificationSettingsStore'

withDefaults(defineProps<{ compact?: boolean }>(), { compact: false })

const { t } = useI18n()
const settings = useNotificationSettingsStore()
const panel = ref()

const MUTED_COLOR = 'var(--p-surface-400)'
const ACTIVE_COLOR = 'var(--p-surface-600)'

const togglePanel = (event: Event) => {
  panel.value?.toggle(event)
}

// El color dice el estado sin abrir el panel: gris cuando hay algo silenciado.
const bellColor = computed(() => (settings.hasMutedNotifications ? MUTED_COLOR : ACTIVE_COLOR))

const tooltip = computed(() =>
  settings.hasMutedNotifications
    ? t('header.notifications.muted_tooltip')
    : t('header.actions.notifications'),
)

const queuedChatSound = computed({
  get: () => settings.queuedChatSound,
  set: (value: boolean) => settings.setQueuedChatSound(value),
})

const queuedChatDesktop = computed({
  get: () => settings.queuedChatDesktop,
  set: (value: boolean) => settings.setQueuedChatDesktop(value),
})
</script>

<template>
  <div>
    <AppButton
      v-tooltip.bottom="tooltip"
      severity="secondary"
      size="small"
      outlined
      rounded
      class="shadow-sm"
      :block="false"
      :style="{ color: bellColor, borderColor: bellColor }"
      :class="
        compact
          ? 'app-button-header-glass !h-8 !w-8 !min-w-8 !p-0'
          : 'app-button-header-glass min-w-9 min-h-9 hover:bg-white/50! dark:hover:bg-[var(--p-primary-600)]/50! dark:text-[var(--p-primary-50)]/90! dark:border-[var(--p-primary-50)]/90!  text-xs!  transition-all duration-200 hover:scale-105 active:scale-95'
      "
      @click="togglePanel"
    >
      <template #icon-start>
        <IconBell :class="compact ? 'h-4 w-4' : 'w-4 h-4'" :stroke-width="1.5" />
      </template>
    </AppButton>

    <Popover
      ref="panel"
      class="!rounded-lg app-card-glass !shadow-xl !backdrop-blur-2xl !backdrop-saturate-150 dark:!border-[var(--p-primary-color)]/30 dark:!bg-[var(--p-primary-950)]/70 [&_.p-popover-content]:!bg-transparent"
    >
      <div class="w-64 p-1">
        <p class="mb-1 text-sm font-semibold text-[var(--p-primary-color)]">
          {{ t('header.notifications.title') }}
        </p>
        <p class="mb-3 text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-100)]">
          {{ t('header.notifications.queued_chats_description') }}
        </p>

        <label class="flex cursor-pointer items-center justify-between gap-3 py-1.5">
          <span class="text-sm text-[var(--p-surface-700)] dark:text-[var(--p-surface-50)]">
            {{ t('header.notifications.queued_chats_sound') }}
          </span>
          <ToggleSwitch v-model="queuedChatSound" />
        </label>

        <label class="flex cursor-pointer items-center justify-between gap-3 py-1.5">
          <span class="text-sm text-[var(--p-surface-700)] dark:text-[var(--p-surface-50)]">
            {{ t('header.notifications.queued_chats_desktop') }}
          </span>
          <ToggleSwitch v-model="queuedChatDesktop" />
        </label>

        <p class="mt-3 text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-100)]">
          {{ t('header.notifications.own_chats_hint') }}
        </p>
      </div>
    </Popover>
  </div>
</template>